import React, { useState, useEffect } from 'react';
import { useScaleData } from '../../hooks/useScaleData';

const HomeScreen = ({ devices, config, isConfigured, onNavigate, user, company, onLogout, loading }) => {
	const [configuredCameras, setConfiguredCameras] = useState([]);
	const [primaryCamera, setPrimaryCamera] = useState(null);
	const [todayStats, setTodayStats] = useState({
		trucksRegistered: 0,
		ticketsCreated: 0,
		totalWeight: 0
	});
	const [statsLoading, setStatsLoading] = useState(true);
	const [syncStatus, setSyncStatus] = useState({ trucksCount: 0, ticketsCount: 0, hasPending: false });
	const [isSyncing, setIsSyncing] = useState(false);
	const { 
		currentWeight, 
		weightStatus,
		setupScaleDataListener, 
		cleanupScaleDataListener,
		handleAutoConnect,
		// Dual scale support
		isDualScaleMode,
		emptyScaleWeight,
		emptyScaleStatus,
		fullScaleWeight,
		fullScaleStatus
	} = useScaleData(config?.scaleType, config);

	// Set up scale data listener when scale connection state changes
	useEffect(() => {
		if (devices.scale.connected) {
			// console.log('🏠 HomeScreen: Scale connected via devices prop');
			setupScaleDataListener();
		} else {
			// console.log('🏠 HomeScreen: Scale disconnected via devices prop');
			cleanupScaleDataListener();
		}
	}, [devices.scale.connected, setupScaleDataListener, cleanupScaleDataListener]);

	// Set up cameras when component mounts
	useEffect(() => {
		if (devices.cameras && devices.cameras.length > 0) {
			// console.log('🏠 HomeScreen: Setting up camera streams');
	
			let configured = [];
			let primary = null; // Track primary camera separately
	
			// Start camera streams for all configured cameras
			devices.cameras.forEach((camera, index) => {
				if (camera.mjpegUrl && window.camera && window.camera.start) {
					// console.log(`🏠 HomeScreen: Starting camera ${index}: ${camera.name}`);
	
					// Set up camera frame listener BEFORE starting the camera
					window.camera.onFrame(camera.id, (jpegBase64) => {
						const cameraElements = document.querySelectorAll(`[data-camera-id="${camera.id}"]`);
						cameraElements.forEach(el => {
							el.src = `data:image/jpeg;base64,${jpegBase64}`;
						});
					});
	
					// Start the camera stream
					window.camera.start(
						camera.mjpegUrl,
						camera.username || '',
						camera.password || '',
						camera.id,
						camera.authType || 'none'
					);
					
					if (camera.isPrimary) {
						primary = camera; // Set primary camera
					} else {
						configured.push(camera);
					}
				}
			});

			// console.log('🏠 HomeScreen: Primary camera:', primary);
	
			// Update state with found cameras
			setPrimaryCamera(primary); // Will be null if no primary camera found
			setConfiguredCameras(configured);
		} else {
			// Clear cameras when devices.cameras is empty or undefined
			setPrimaryCamera(null);
			setConfiguredCameras([]);
		}
	
		// Cleanup function to stop camera streams
		return () => {
			if (configuredCameras.length > 0 && window.camera && window.camera.stop) {
				configuredCameras.forEach(camera => {
					// console.log('🏠 HomeScreen: Stopping camera stream:', camera.name);
					window.camera.stop(camera.id);
				});
			}
	
			if (primaryCamera && window.camera && window.camera.stop) {
				// console.log('🏠 HomeScreen: Stopping primary camera stream');
				window.camera.stop(primaryCamera.id);
			}
		};
	}, [devices.cameras]);

	// Fetch today's activity stats
	useEffect(() => {
		fetchTodayStats();
	}, [company]);

	// Check sync status on mount and set up listeners
	useEffect(() => {
		const checkSyncStatus = async () => {
			if (window.electronAPI && window.electronAPI.secureStorage) {
				const status = await window.electronAPI.secureStorage.getSyncQueueStatus();
				setSyncStatus(status);
			}
		};

		checkSyncStatus();

		// Listen for sync completion events
		if (window.electronAPI && window.electronAPI.onMessage) {
			const handleSyncCompleted = (status) => {
				//console.log('🔄 Sync completed event received:', status);
				setSyncStatus(status);
				setIsSyncing(false);
			};

			const handleTruckSynced = (data) => {
				//console.log('✅ Truck synced:', data);
				// Refresh sync status
				checkSyncStatus();
			};

			const handleTicketSynced = (data) => {
				//console.log('✅ Ticket synced:', data);
				// Refresh sync status and stats
				checkSyncStatus();
				fetchTodayStats();
			};

			window.electronAPI.onMessage('sync-completed', handleSyncCompleted);
			window.electronAPI.onMessage('truck-synced', handleTruckSynced);
			window.electronAPI.onMessage('ticket-synced', handleTicketSynced);
		}

		// Refresh sync status periodically
		const interval = setInterval(checkSyncStatus, 30000); // Every 30 seconds

		return () => {
			clearInterval(interval);
		};
	}, []);

	const fetchTodayStats = async () => {
		if (!company?.Id) {
			setStatsLoading(false);
			return;
		}

		setStatsLoading(true);
		try {
			// Fetch today's stats from the dedicated endpoint
			const statsResponse = await window.electronAPI.request(
				`/companies/${company.Id}/dailystats`,
				'GET',
				null,
				{
					'Accept': 'application/json',
				}
			);

			if (statsResponse.ok) {
				const statsData = await statsResponse.body;
				// console.log('✅ Today\'s stats fetched:', statsData);

				setTodayStats({
					trucksRegistered: statsData.TrucksCount || 0,
					ticketsCreated: statsData.TicketCount || 0,
					totalWeight: statsData.TotalWeight || 0
				});
			} else {
				console.error('Failed to fetch today\'s stats');
			}
		} catch (error) {
			console.error('Error fetching today\'s stats:', error);
		} finally {
			setStatsLoading(false);
		}
	};

	const handleManualSync = async () => {
		if (isSyncing) return;

		setIsSyncing(true);
		// console.log('🔄 Manual sync triggered from HomeScreen');

		try {
			if (window.electronAPI && window.electronAPI.triggerSync) {
				const status = await window.electronAPI.triggerSync();
				setSyncStatus(status);
				
				if (!status.hasPending) {
					alert('No pending data to sync.');
				}
			}
		} catch (error) {
			console.error('Manual sync error:', error);
			alert('Failed to trigger sync. Please check your connection.');
		} finally {
			setIsSyncing(false);
		}
	};

	const actionButtons = [
		{
			id: 'register',
			icon: 'fas fa-truck',
			title: 'Register Truck',
			description: 'Add new trucks to the system',
			buttonClass: 'btn-primary',
			disabled: !isConfigured,
			screen: 'register',
			color: '#007bff',
			gridArea: 'btn-reg',
		},
		{
			id: 'ticket',
			icon: 'fas fa-receipt',
			title: 'Create Ticket',
			description: 'Generate weight tickets for deliveries',
			buttonClass: 'btn-success',
			disabled: !isConfigured,
			screen: 'ticket',
			color: '#28a745',
			gridArea: 'btn-tkt'
		}
	];

	return (
		<div className="screen home">
			{/* HEADER */}
			<div className='header' style={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				padding: '1rem',
				background: '#f8f9fa',
				borderRadius: '8px',
				border: '1px solid #e9ecef'
			}}>
				<div>
					<h1 style={{ margin: 0 }}>Control Center</h1>
					<div style={{ fontSize: '0.875rem', color: '#6c757d', marginTop: '0.25rem' }}>
						Welcome, {user?.FirstName || user?.Email || 'User'} • {company?.Name || company?.CompanyName || 'Unknown Company'}
					</div>
				</div>
				<button
					className="btn btn-secondary btn-small"
					onClick={onLogout}
					disabled={loading}
					style={{
						padding: '0.5rem 1rem',
						fontSize: '0.875rem'
					}}
				>
					{loading ? 'Signing out...' : 'Sign Out'}
				</button>
			</div>

			{/* ACTION BUTTONS */}
			{/* <div className="action-buttons"> */}
			{actionButtons.map(button => (
				<div key={button.id} className={`card ${button.gridArea}`} onClick={button.disabled ? null : () => onNavigate(button.screen)} style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					textAlign: 'center',
					padding: '1rem',
					cursor: button.disabled ? 'not-allowed' : 'pointer',
					opacity: button.disabled ? 0.6 : 1,
					transition: 'all 0.2s ease',
					backgroundColor: button.color,
					color: '#fff'
				}}>
					<div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
						<i className={button.icon}></i>
					</div>
					<h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#fff' }}>
						{button.title}
					</h3>
					<p style={{ margin: '0 0 1.5rem 0' }}>
						{button.description}
					</p>
				</div>
			))}

			{!isConfigured && (
				<div className="alert alert-warning" style={{ marginTop: '2rem', marginBottom: '0'}}>
					<strong><i className="fas fa-exclamation-triangle"></i> Configuration Required</strong>
					<p>Please configure printer, scale, and company/location before using the system. Cameras are optional.</p>
				</div>
			)}

			<div className="card status" style={{ position: 'relative' }}>
				<div style={{
					position: 'absolute',
					top: '0.5rem',
					right: '0.5rem',
					zIndex: 1
				}}>
					<button
						className="btn btn-transparent btn-small"
						onClick={() => onNavigate('config')}
						style={{
							padding: '0.25rem 0.5rem',
							fontSize: '0.875rem',
							color: '#6c757d',
							cursor: 'pointer'
						}}
						title="Configure System"
					>
						<i className="fas fa-cog"></i>
					</button>
				</div>
				<h4 style={{ marginBottom: '0.5rem' }}>Hardware Status</h4>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<span>Printer:</span>
						<span className={devices.printer.connected ? 'text-success' : 'text-danger'}>
							{devices.printer.connected ? <><i className="fas fa-check-circle" style={{ color: '#28a745' }}></i> Connected</> : <><i className="fas fa-times-circle"></i> Disconnected</>}
						</span>
					</div>
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<span>Scale:</span>
						<span className={devices.scale.connected ? 'text-success' : 'text-danger'}>
							{devices.scale.connected ? <><i className="fas fa-check-circle" style={{ color: '#28a745' }}></i> Connected</> : <><i className="fas fa-times-circle"></i> Disconnected</>}
						</span>
					</div>
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<span>Cameras:</span>
						<span className={devices.cameras.length > 0 ? 'text-success' : 'text-info'}>
							{devices.cameras.length > 0 ? <><i className="fas fa-check-circle" style={{ color: '#28a745' }}></i> {devices.cameras.length} Connected</> : <><i className="fas fa-info-circle" style={{ color: '#17a2b8' }}></i> Optional</>}
						</span>
					</div>
				</div>
			</div>

			<div className="card activity">
				<h4 style={{ marginBottom: '0.5rem' }}>Today's Activity</h4>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
					{statsLoading ? (
						<div style={{ textAlign: 'center', padding: '1rem' }}>
							<i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', color: '#6c757d' }}></i>
							<div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6c757d' }}>Loading stats...</div>
						</div>
					) : (
						<>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span>Trucks Registered:</span>
								<span style={{ fontWeight: 'bold', color: '#007bff' }}>
									{todayStats.trucksRegistered}
								</span>
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span>Tickets Created:</span>
								<span style={{ fontWeight: 'bold', color: '#28a745' }}>
									{todayStats.ticketsCreated}
								</span>
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span>Total Weight:</span>
								<span style={{ fontWeight: 'bold', color: '#fd7e14' }}>
									{todayStats.totalWeight} Tons
								</span>
							</div>
							<div>
								<button
									className="btn btn-info btn-small"
									onClick={() => onNavigate('tickets')}
									style={{
										padding: '0.5rem 1rem',
										fontSize: '0.875rem',
										float: 'right'
									}}
									> Manage Tickets
								</button>
							</div>
						</>
					)}
				</div>
			</div>

			{/* Sync Status Card */}
			{syncStatus.hasPending && (
				<div className="card" style={{ 
					gridArea: 'sync',
					position: 'absolute',
					bottom: '2rem',
					right: '2rem',
					zIndex: 1,
					background: '#fff3cd',
					border: '1px solid #ffc107'
				}}>
					<h4 style={{ marginBottom: '0.5rem', color: '#856404' }}>
						<i className="fas fa-sync"></i> Pending Sync
					</h4>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
						{syncStatus.trucksCount > 0 && (
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span>Trucks to Sync:</span>
								<span style={{ fontWeight: 'bold', color: '#856404' }}>
									{syncStatus.trucksCount}
								</span>
							</div>
						)}
						{syncStatus.ticketsCount > 0 && (
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span>Tickets to Sync:</span>
								<span style={{ fontWeight: 'bold', color: '#856404' }}>
									{syncStatus.ticketsCount}
								</span>
							</div>
						)}
						<div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#856404' }}>
							<i className="fas fa-info-circle"></i> Auto-sync runs every 5 minutes
						</div>
						<button
							className="btn btn-warning btn-small"
							onClick={handleManualSync}
							disabled={isSyncing}
							style={{ marginTop: '0.5rem', width: '100%' }}
						>
							{isSyncing ? (
								<><i className="fas fa-spinner fa-spin"></i> Syncing...</>
							) : (
								<><i className="fas fa-sync"></i> Sync Now</>
							)}
						</button>
					</div>
				</div>
			)}

			{/* {!isConfigured && (
				<div className="alert alert-warning" style={{ marginTop: '2rem', marginBottom: '0'}}>
					<strong><i className="fas fa-exclamation-triangle"></i> Configuration Required</strong>
					<p>Please configure all devices before using the system.</p>
				</div>
			)} */}

			{/* Single Scale Display */}
			{!isDualScaleMode && (
				<div className="weight-display scale" id="weightReading" style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					padding: '1rem',
					background: '#333',
					borderRadius: '8px',
					border: '1px solid #e9ecef',
					margin: '0'
				}}>
					<h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: 'inherit' }}><i className="fas fa-weight-hanging"></i> Live Scale Reading</h3>
					{currentWeight ? (
						<div style={{
							fontSize: '2.5rem',
							fontWeight: 'bold',
							height: '50px',
							textShadow: '0 2px 4px rgba(0,0,0,0.3)'
						}}>
							{currentWeight}
							<span style={{
								fontSize: '1rem',
								opacity: 0.8,
								className: weightStatus === 'unstable' ? 'unstable' : ''
							}}>
							</span>
						</div>
					) : (
						<div style={{
							fontSize: '1.5rem',
							opacity: 0.7,
							height: '50px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontStyle: 'italic'
						}}>
							Waiting for weight data...
						</div>
					)}
					<div style={{
						fontSize: '0.875rem',
						opacity: 0.8,
						marginTop: '0.5rem'
					}}>
						{weightStatus === 'unstable' ? 'Weight unstable - please wait for stable reading' : 'Weight stable'}
					</div>
				</div>
			)}

			{/* Dual Scale Display */}
			{isDualScaleMode && (
				<div className="scale" style={{ display: 'flex', gap: '2rem' }}>
					{/* Empty Scale */}
					<div className="weight-display" id="emptyWeightReading" style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						padding: '1rem',
						//background: '#007bff',
						borderRadius: '8px',
						border: '1px solid #e9ecef',
						margin: '0',
						// color: 'white',
						flex: 1
					}}>
						<h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: 'inherit' }}>
							<i className="fas fa-truck"></i> Empty Scale (Entrance)
						</h3>
						{emptyScaleWeight ? (
							<div style={{
								fontSize: '2.5rem',
								fontWeight: 'bold',
								height: '50px',
								textShadow: '0 2px 4px rgba(0,0,0,0.3)'
							}}>
								{emptyScaleWeight}
							</div>
						) : (
							<div style={{
								fontSize: '1.5rem',
								opacity: 0.7,
								height: '50px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontStyle: 'italic'
							}}>
								Waiting for weight data...
							</div>
						)}
						<div style={{
							fontSize: '0.875rem',
							opacity: 0.8,
							marginTop: '0.5rem'
						}}>
							{emptyScaleStatus === 'unstable' ? 'Weight unstable - please wait for stable reading' : 'Weight stable'}
						</div>
					</div>

					{/* Full Scale */}
					<div className="weight-display" id="fullWeightReading" style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						padding: '1rem',
						//background: '#28a745',
						borderRadius: '8px',
						border: '1px solid #e9ecef',
						margin: '0',
						// color: 'white',
						flex: 1
					}}>
						<h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: 'inherit' }}>
							<i className="fas fa-truck-loading"></i> Full Scale (Exit)
						</h3>
						{fullScaleWeight ? (
							<div style={{
								fontSize: '2.5rem',
								fontWeight: 'bold',
								height: '50px',
								textShadow: '0 2px 4px rgba(0,0,0,0.3)'
							}}>
								{fullScaleWeight}
							</div>
						) : (
							<div style={{
								fontSize: '1.5rem',
								opacity: 0.7,
								height: '50px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontStyle: 'italic'
							}}>
								Waiting for weight data...
							</div>
						)}
						<div style={{
							fontSize: '0.875rem',
							opacity: 0.8,
							marginTop: '0.5rem'
						}}>
							{fullScaleStatus === 'unstable' ? 'Weight unstable - please wait for stable reading' : 'Weight stable'}
						</div>
					</div>
				</div>
			)}


			{/* Front Camera (Primary) */}
			{ primaryCamera && (
				<div
				key={primaryCamera?.id}
				className="cam1">
				<div
					style={{
						width: '100%',
						// height: '100%',
						border: '2px solid #e2e8f0',
						borderRadius: '8px',
						overflow: 'hidden',
						background: '#f8f9fa',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						position: 'relative',
					}}
				>
					<img
						data-camera-id={primaryCamera?.id}
						src=""
						alt={`Camera ${primaryCamera?.name}`}
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'contain'
						}}
					/>
					<div className="camera-overlay" style={{
						position: 'absolute',
						bottom: '0',
						left: '0',
						right: '0',
						background: 'rgba(0,0,0,0.7)',
						color: 'white',
						padding: '0.75rem',
						fontSize: '0.875rem'
					}}>
						{`${primaryCamera?.name} - Primary` || `Camera ${primaryCamera?.id}`}
					</div>
				</div>
				</div>
			)}

			{/* </div> */}
			{/* Additional Cameras in 2x2 Grid */}
			{configuredCameras.length > 0 && (
				configuredCameras.map((camera, index) => {
					const camNum = index+2;
					return (
					<div
						key={camera.id}
						className={`cam${camNum}`}
						style={{ 
							background: '#808080',
						 	borderRadius: '8px',
							border: '2px solid #e2e8f0',
							alignContent: 'center'
						}}
					>
						<div
							style={{
								width: '100%',
								overflow: 'hidden',
								background: '#f8f9fa',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								position: 'relative',
							}}
						>
							<img
								data-camera-id={camera.id}
								src=""
								alt={`Camera ${camera.name}`}
								style={{
									width: '100%',
									height: '100%',
									objectFit: 'contain',
								}}
							/>
							<div
								className="camera-overlay"
								style={{
									position: 'absolute',
									bottom: '0',
									left: '0',
									right: '0',
									background: 'rgba(0,0,0,0.7)',
									color: 'white',
									padding: '0.75rem',
									fontSize: '0.875rem',
								}}
							>
								{camera.name || `Camera ${camera.id}`}
							</div>
						</div>
					</div>
				)})
			)}

		</div>
	);
};

export default HomeScreen;